import React, { useState, useMemo } from 'react';
import generateUUID from 'utils/sharedUtils/generateUUID';
import NotificationRecipients from './NotificationRecipients';
import { LockedUpgradeOption } from './LockedUpgradeOption';
import validateEmailAddress from 'utils/validateEmailAddress';
import validatePagerAddress from 'utils/validatePagerAddress';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { Modal } from 'react-bootstrap';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import ManageTeamLocation from './ManageTeamLocation';
import { toast } from 'react-toastify';
import ReactPaginate from 'react-paginate';
import { useTeamMemberFilters } from '../../hooks/useTeam';
import { getInitials } from '../../helpers/intials';

export default function NewDistributionListDialog({
  show,
  roster,
  onClose,
  recipientTypes,
  distributionLists,
  teamAor,
}) {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [SMSRecipients, setSMSRecipients] = useState([]);
  const [TTSHomeRecipients, setTTSHomeRecipients] = useState([]);
  const [TTSCellRecipients] = useState([]);
  const [pagerRecipients, setPagerRecipients] = useState([]);
  const [selectedAor, setSelectedAor] = useState(teamAor || null);
  const [templateTitle, setTemplateTitle] = useState();
  const [description, setDescription] = useState('');
  const [searchTermInTeam, setSearchTermInTeam] = useState('');
  const [searchTermAvailable, setSearchTermAvailable] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [currentPageInTeam, setCurrentPageInTeam] = useState(1);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const { membersInTeam, availableMembers } = useTeamMemberFilters({
    roster,
    selectedMembers,
    searchTermInTeam,
    searchTermAvailable,
  });

  const totalPages = Math.ceil(availableMembers.length / pageSize);
  const paginatedAvailableMembers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return availableMembers.slice(start, start + pageSize);
  }, [availableMembers, currentPage]);

  const totalPagesInTeam = Math.ceil(membersInTeam.length / pageSize);
  const paginatedMembersInTeam = useMemo(() => {
    const start = (currentPageInTeam - 1) * pageSize;
    return membersInTeam.slice(start, start + pageSize);
  }, [membersInTeam, currentPageInTeam]);

  React.useEffect(() => {
    setCurrentPageInTeam(1);
  }, [searchTermInTeam]);

  const handleMemberToggle = (member) => {
    const isSelected = selectedMembers.some(
      (m) => m.user_guid === member.user_guid
    );
    if (isSelected) {
      setSelectedMembers(
        selectedMembers.filter((m) => m.user_guid !== member.user_guid)
      );
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
    setHasInteracted(true);
  };

  const handleSelectAllAvailable = () => {
    const allSelected = availableMembers.every((member) =>
      selectedMembers.some((m) => m.user_guid === member.user_guid)
    );
    if (allSelected) {
      setSelectedMembers(
        selectedMembers.filter(
          (m) => !availableMembers.some((am) => am.user_guid === m.user_guid)
        )
      );
    } else {
      setSelectedMembers([
        ...selectedMembers,
        ...availableMembers.filter(
          (am) => !selectedMembers.some((m) => m.user_guid === am.user_guid)
        ),
      ]);
    }
    setHasInteracted(true);
  };

  const handleNextStep = () => {
    if (templateTitle) {
      setCurrentStep(2);
      setHasInteracted(true);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const getUniqueValidations = (recipients, check) => {
    const recipientValidations = recipients
      .map((recipient) => check(recipient.content))
      .filter((invalid) => invalid.length)
      .reduce((acc, item) => {
        item.forEach((inner) => acc.push(inner));
        return acc;
      }, []);

    return [...new Set(recipientValidations)];
  };

  const onAddMembersClick = () => {
    const SMSValidations = getUniqueValidations(
      SMSRecipients,
      getSMSValidationsForItem
    );
    const emailValidations = getUniqueValidations(
      emailRecipients,
      getEmailValidationsForItem
    );
    const TTSCellValidations = getUniqueValidations(
      TTSCellRecipients,
      getTTSValidationsForItem
    );
    const TTSHomeValidations = getUniqueValidations(
      TTSHomeRecipients,
      getTTSValidationsForItem
    );
    const pagerValidations = getUniqueValidations(
      pagerRecipients,
      getPagerValidationsForItem
    );

    const aggregateValidations = [
      ...SMSValidations,
      ...emailValidations,
      ...TTSCellValidations,
      ...TTSHomeValidations,
      ...pagerValidations,
    ];

    if (aggregateValidations.length > 0) {
      toast.error(
        <div>
          {aggregateValidations.map((text) => (
            <>
              - {text}
              <br />
            </>
          ))}
        </div>
      );
    } else {
      const newList = {
        id: generateUUID(),
        name: templateTitle,
        description: description,
        members: selectedMembers.map((member) => member.user_guid),
        nonMembers: [
          ...emailRecipients,
          ...SMSRecipients,
          ...TTSHomeRecipients,
          ...pagerRecipients,
        ],
        aor: selectedAor,
      };
      const currentList = distributionLists.findIndex(
        (ele) => ele.name === newList.name
      );
      if (currentList !== -1) {
        const errorMessage =
          `Duplicate Relevant Team Name.\n\n` +
          'Failed Create New Relevant Team';
        console.warn(errorMessage);
        toast.error(errorMessage);
        return;
      } else {
        onClose(newList);
      }
    }
  };

  function getEmailValidationsForItem(item) {
    let validation_messages = [];
    if (!item) {
      validation_messages = [...validation_messages, `Email cannot be blank`];
    } else if (!validateEmailAddress(item)) {
      validation_messages = [...validation_messages, `Invalid email address`];
    }
    return validation_messages;
  }

  function getSMSValidationsForItem(item) {
    let validation_messages = [];
    if (!item) {
      validation_messages = [
        ...validation_messages,
        `SMS number cannot be blank`,
      ];
    } else if (
      !item.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/g)
    ) {
      validation_messages = [...validation_messages, `Invalid SMS number`];
    }
    return validation_messages;
  }

  function getTTSValidationsForItem(item) {
    let validation_messages = [];
    if (!item) {
      validation_messages = [
        ...validation_messages,
        `Text-To-Speech number cannot be blank`,
      ];
    } else if (
      !item.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/g)
    ) {
      validation_messages = [
        ...validation_messages,
        `Invalid Text-To-Speech number`,
      ];
    }
    return validation_messages;
  }

  function getPagerValidationsForItem(item) {
    let validation_messages = [];
    if (!item) {
      validation_messages = [
        ...validation_messages,
        `Pager address cannot be blank`,
      ];
    } else if (!validatePagerAddress(item)) {
      validation_messages = [...validation_messages, `Invalid pager address`];
    }
    return validation_messages;
  }

  return (
    <Modal
      show={show}
      onHide={() => onClose()}
      dialogClassName="modal-90w"
      aria-labelledby="new-functional-team-modal-title"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title id="new-functional-team-modal-title">
          New Relevant Team
        </Modal.Title>
        <div className="step-indicator">Step {currentStep} of 2</div>
      </Modal.Header>
      <Modal.Body>
        <div className="edit-ics-team-form">
          {currentStep === 1 ? (
            <>
              {/* Step 1: Team Information and Additional Recipients */}
              <div className="team-info-section">
                <div className="form-group">
                  <label className="form-label">Team Title</label>
                  <StylishNewInput
                    value={templateTitle}
                    onChange={(e) => {
                      setTemplateTitle(e.target.value);
                      setHasInteracted(true);
                    }}
                    placeholder="Enter team title"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setHasInteracted(true);
                    }}
                    placeholder="Enter team description (optional)"
                    className="form-control"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <ManageTeamLocation
                    selectedAor={selectedAor}
                    onChangeAor={setSelectedAor}
                  />
                </div>
              </div>

              {/* Additional Recipients Section */}
              {!!recipientTypes.length && (
                <>
                  <label className="form-label">Additional Recipients</label>
                  <hr className="dashed" />
                  <div className="additional-recipients-section">
                    <div className="recipients-content">
                      {!!recipientTypes.find((t) => t === 'Email') && (
                        <>
                          <div className="recipient-type-section">
                            <span className="form-label recipient-type-label">
                              Email Recipients
                            </span>
                            <NotificationRecipients
                              recipients={emailRecipients}
                              onRecipientsChange={(newRecipients) =>
                                setEmailRecipients(newRecipients)
                              }
                              getValidationMessagesForItem={
                                getEmailValidationsForItem
                              }
                              type="emailRecipients"
                            />
                          </div>
                          {recipientTypes.findIndex((i) => i === 'Email') !==
                          recipientTypes.length - 1 ? (
                            <hr className="dashed my-4" />
                          ) : null}
                        </>
                      )}

                      {!!recipientTypes.find((t) => t === 'SMS') && (
                        <>
                          <div className="recipient-type-section">
                            <span className="form-label recipient-type-label">
                              SMS Recipients
                            </span>
                            <NotificationRecipients
                              recipients={SMSRecipients}
                              onRecipientsChange={(newRecipients) =>
                                setSMSRecipients(newRecipients)
                              }
                              getValidationMessagesForItem={
                                getSMSValidationsForItem
                              }
                              type="SMSRecipients"
                            />
                          </div>
                          {recipientTypes.findIndex((i) => i === 'SMS') !==
                          recipientTypes.length - 1 ? (
                            <hr className="dashed my-4" />
                          ) : null}
                        </>
                      )}

                      {!!recipientTypes.find((t) => t === 'Pager') && (
                        <>
                          <div className="recipient-type-section">
                            <span className="form-label recipient-type-label">
                              Pager Recipients
                            </span>
                            <NotificationRecipients
                              recipients={pagerRecipients}
                              onRecipientsChange={(newRecipients) =>
                                setPagerRecipients(newRecipients)
                              }
                              getValidationMessagesForItem={
                                getPagerValidationsForItem
                              }
                              type="pagerRecipients"
                            />
                          </div>
                          {recipientTypes.findIndex((i) => i === 'Pager') !==
                          recipientTypes.length - 1 ? (
                            <hr className="dashed my-4" />
                          ) : null}
                        </>
                      )}

                      {!!recipientTypes.find(
                        (t) => t === 'Text-To-Speech (cell)'
                      ) && (
                        <>
                          <div className="recipient-type-section">
                            <LockedUpgradeOption label="Voice (TTS cell)" />
                          </div>
                          {recipientTypes.findIndex(
                            (i) => i === 'Text-To-Speech (cell)'
                          ) !==
                          recipientTypes.length - 1 ? (
                            <hr className="dashed my-4" />
                          ) : null}
                        </>
                      )}

                      {!!recipientTypes.find(
                        (t) => t === 'Text-To-Speech (home)'
                      ) && (
                        <>
                          <div className="recipient-type-section">
                            <span className="form-label recipient-type-label">
                              Text-To-Speech (home) Recipients
                            </span>
                            <NotificationRecipients
                              recipients={TTSHomeRecipients}
                              onRecipientsChange={(newRecipients) =>
                                setTTSHomeRecipients(newRecipients)
                              }
                              getValidationMessagesForItem={
                                getTTSValidationsForItem
                              }
                              type="TTSHomeRecipients"
                            />
                          </div>
                          {recipientTypes.findIndex(
                            (i) => i === 'Text-To-Speech (home)'
                          ) !==
                          recipientTypes.length - 1 ? (
                            <hr className="dashed my-4" />
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                  <hr className="dashed" />
                </>
              )}
            </>
          ) : (
            <>
              {/* Step 2: Member Selection */}
              <div className="member-selection-section">
                {/* Members in Team */}
                <div className="members-in-team-section">
                  <label className="form-label">Members in Team</label>
                  <StylishNewSearchBar
                    value={searchTermInTeam}
                    onChangeSearchTerm={setSearchTermInTeam}
                    placeholder="Search Members"
                    className="member-search"
                  />
                  <div className="members-list">
                    {paginatedMembersInTeam.length === 0 && (
                      <div className="empty-list">No members in team.</div>
                    )}
                    {paginatedMembersInTeam.map((member) => (
                      <div key={member.user_guid} className="member-item">
                        <StylishNewCheckbox
                          checked={true}
                          onClick={() => handleMemberToggle(member)}
                        />
                        <div className="member-avatar">
                          <div className="rounded-circle avatar-initials sml">
                            {getInitials(
                              member.user_name || member.email_address || ''
                            )}
                          </div>
                        </div>
                        <div className="member-info">
                          <div className="member-main-row">
                            <span className="member-name">
                              {member.user_name || '-'}
                            </span>
                            <span className="member-dot-separator">&bull;</span>
                            <span className="member-email">
                              {member.email_address || '-'}
                            </span>
                          </div>
                          <div className="member-role">
                            {member.role_string || '-'}
                          </div>
                        </div>
                      </div>
                    ))}
                    {totalPagesInTeam > 1 && (
                      <div className="d-flex justify-content-center justify-content-md-end mt-4">
                        <ReactPaginate
                          className="pagination"
                          breakLabel="..."
                          nextLabel=">"
                          previousLabel="<"
                          onPageChange={({ selected }) =>
                            setCurrentPageInTeam(selected + 1)
                          }
                          pageRangeDisplayed={3}
                          pageCount={totalPagesInTeam}
                          forcePage={currentPageInTeam - 1}
                          renderOnZeroPageCount={null}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Members */}
                <div className="available-members-section">
                  <label className="form-label">Available Members</label>
                  <StylishNewSearchBar
                    value={searchTermAvailable}
                    onChangeSearchTerm={setSearchTermAvailable}
                    placeholder="Search Members"
                    className="member-search"
                  />
                  <div className="select-all-container">
                    <button
                      type="button"
                      className="select-all-link"
                      onClick={handleSelectAllAvailable}
                    >
                      Select All
                    </button>
                  </div>
                  <div className="members-list">
                    {paginatedAvailableMembers.length === 0 && (
                      <div className="empty-list">No available members.</div>
                    )}
                    {paginatedAvailableMembers.map((member) => {
                      const isSelected = selectedMembers.some(
                        (m) => m.user_guid === member.user_guid
                      );
                      return (
                        <div key={member.user_guid} className="member-item">
                          <StylishNewCheckbox
                            checked={isSelected}
                            onClick={() => handleMemberToggle(member)}
                          />
                          <div className="member-avatar">
                            <div className="rounded-circle avatar-initials sml">
                              {getInitials(
                                member.user_name || member.email_address || ''
                              )}
                            </div>
                          </div>
                          <div className="member-info">
                            <div className="member-main-row">
                              <span className="member-name">
                                {member.user_name || '-'}
                              </span>
                              <span className="member-dot-separator">
                                &bull;
                              </span>
                              <span className="member-email">
                                {member.email_address || '-'}
                              </span>
                            </div>
                            <div className="member-role">
                              {member.role_string || '-'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center justify-content-md-end mt-4">
                      <ReactPaginate
                        className="pagination"
                        breakLabel="..."
                        nextLabel=">"
                        previousLabel="<"
                        onPageChange={({ selected }) =>
                          setCurrentPage(selected + 1)
                        }
                        pageRangeDisplayed={3}
                        pageCount={totalPages}
                        forcePage={currentPage - 1}
                        renderOnZeroPageCount={null}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {currentStep === 1 ? (
          <>
            <StylishNewButton
              className="button--secondary"
              onClick={() => onClose()}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              className="button--primary"
              onClick={handleNextStep}
              disabled={!templateTitle}
            >
              Next
            </StylishNewButton>
            {hasInteracted && !templateTitle && (
              <span className="form-text form-error">
                Team title is required
              </span>
            )}
          </>
        ) : (
          <>
            <StylishNewButton
              className="button--secondary"
              onClick={handlePreviousStep}
            >
              Back
            </StylishNewButton>
            <StylishNewButton
              className="button--primary"
              onClick={onAddMembersClick}
              disabled={
                !(
                  templateTitle &&
                  (selectedMembers.length ||
                    emailRecipients.length ||
                    SMSRecipients.length ||
                    TTSHomeRecipients.length ||
                    TTSCellRecipients.length ||
                    pagerRecipients.length)
                )
              }
            >
              Save Team
            </StylishNewButton>
            {hasInteracted &&
              !(
                templateTitle &&
                (selectedMembers.length ||
                  emailRecipients.length ||
                  SMSRecipients.length ||
                  TTSHomeRecipients.length ||
                  TTSCellRecipients.length ||
                  pagerRecipients.length)
              ) && (
                <span className="form-text form-error">
                  Form Validation Error
                </span>
              )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
